import type {PostStateFilter} from '@Components/social-media/social-media.types';
import {SocialPostEvent} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import {getNumPublishingParams, setIsRescheduled, setSocialPostDialogStatus} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.reducer';
import {addSocialPostToGrid, deleteSocialPost, mergeRefreshedPosts} from '@Components/social-media/social-media-reducer';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';

import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import type {SocialPostsDialogModalProps} from '@Modals/social-posts-dialog-modal';
import {openSocialPostsDialogModal} from '@Modals/social-posts-dialog-modal';
import type {ReactElement} from 'react';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import type {VoidFunction} from '@/utils/general.util';
import {isModalOpen} from '@Components/modal-container';
import {isPromotingEvent, trackEventPromotionScheduledForPlatform} from '@Libraries/social-media-event-promotion.library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {mapIndexToAccountPublishingParams, getPublishUrlFromPublishParams} from '@Libraries/social-media-params.library';

export enum SocialPostGridItemAction {
  EDIT,
  VIEW_POST,
  SCHEDULE,
  CANCEL_SCHEDULE,
  PUBLISH,
  DUPLICATE,
  DELETE,
  DELETE_PERMANENTLY,
  RESTORE,
  VIEW_PERFORMANCE,
  PROMOTE_EVENT,
  SHARE_IN_TEAM,
}

export interface SocialPostActionData {
  key: string;
  type: SocialPostGridItemAction;
  iconClasses: string;
  iconColorClasses?: string;
  text: string;
  textTypographyClasses: string;
  textColorClasses: string;
  handler: VoidFunction;
  afterHeadingTextContent?: ReactElement<any>;
}

export const isPostVisibleForFilter = (postState: PostState, postStateFilter: PostStateFilter): boolean => {
  switch (postState) {
    case PostState.DRAFT:
    case PostState.PUBLISHING:
    case PostState.READY:
    case PostState.DELETED:
      return postStateFilter[PostState.DRAFT];
    case PostState.SCHEDULED:
    case PostState.SCHEDULE_READY:
      return postStateFilter[PostState.SCHEDULED];
    case PostState.PUBLISHED:
      return postStateFilter[PostState.PUBLISHED];
    case PostState.ERROR:
    case PostState.SCHEDULE_ERROR:
    case PostState.WAITING_FOR_INSTAGRAM:
      return postStateFilter[PostState.ERROR];
    default:
      return false;
  }
};

export const isPostScheduled = (state: PostState): boolean => {
  return state === PostState.SCHEDULING || state === PostState.SCHEDULE_READY;
};

export const isPostPublished = (state: PostState): boolean => {
  return state === PostState.PUBLISHED;
};

export const shouldShowShareInTeamForPost = (post: SocialPost): boolean => {
  return post.userId === getUserId();
};

export const isPostEditable = (state: PostState): boolean => {
  return (
    state === PostState.ERROR ||
    state === PostState.DRAFT ||
    state === PostState.READY ||
    state === PostState.SCHEDULE_ERROR ||
    state === PostState.SCHEDULED ||
    state === PostState.SCHEDULE_READY
  );
};

export const convertDateToUTCTimestamp = (date: Date): number => {
  return Date.parse(date.toUTCString()) / 1000;
};

export const deletePostSchedule = (post: SocialPost): void => {
  const postId = post.id;
  void window.PMW.write(window.PMW.util.site_url('socialmedia/unschedulePost'), {idPost: post.id}, (r: any): void => {
    if (r.status === 'success') {
      getPostById(postId, SocialPostEvent.UNSCHEDULED);
    } else {
      window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
    }
  });
};

export const viewPostOnSocialNetworks = (post: SocialPost): void => {
  try {
    openPostsOnSocialNetworks(post);
  } catch (error) {
    window.PMW.showMessageDialog(window.i18next.t('pmwjs_view_posts_pop_ups_blocked'), window.i18next.t('pmwjs_view_posts_pop_ups_blocked_message'));
  }
};

export const openPostsOnSocialNetworks = (post: SocialPost): void => {
  const totalPublishingParams = getNumPublishingParams(post);
  let popUpBlocked = false;

  for (let i = 0; i < totalPublishingParams; i++) {
    const accountInformation = mapIndexToAccountPublishingParams(post, i);
    const {account} = accountInformation;
    const {platformMediaType} = accountInformation;

    const window = viewPostOnSocialNetworkForAccountWithMediaType(post, account, platformMediaType);

    if (window === null || typeof window === 'undefined') {
      popUpBlocked = true;
    }
  }

  if (popUpBlocked) {
    throw new Error('Popup(s) blocked.');
  }
};

export const viewPostOnSocialNetworkForAccountWithMediaType = (post: SocialPost, account: SocialAccount, platformMediaType: PlatformMediaType): Window | null => {
  const publishingParams = post.publishingParams[account.id][platformMediaType];
  const url = getPublishUrlFromPublishParams(publishingParams);
  return window.open(url, '_blank');
};

export const processPostToEnableMetrics = (post: SocialPost): void => {
  if (post.state !== PostState.PUBLISHED) {
    return;
  }

  void window.PMW.write(window.PMW.util.site_url('socialmedia/enablePostMetrics'), {postId: post.id}, (r: any): void => {
    if (r.status === 'success') {
      window.dispatchEvent(
        new CustomEvent(SocialPostEvent.UPDATED, {
          detail: {
            postId: post.id,
            post: r.data.post,
          },
        })
      );
    }
  });
};

export const deleteWithEndpoint = async (endpoint: string, postId: string): Promise<void> => {
  showLoading('delete-post', {
    text: window.i18next.t('pmwjs_deleting'),
  });

  try {
    await window.PMW.writeLocal(endpoint, {
      idPost: postId,
    });
    window.PMW.redux.store.dispatch(deleteSocialPost(postId));
    triggerSocialPostEvent(SocialPostEvent.DELETED, {postId});
  } catch (e) {
    window.PMW.openErrorModal({
      message: window.i18next.t('pmwjs_social_post_delete_error'),
    });
    return;
  } finally {
    hideLoading('delete-post');
  }
};

export const schedulePostByTimestamp = (postId: string, timezoneOffset: number, publishOn: number): void => {
  void window.PMW.write(
    window.PMW.util.site_url('socialmedia/rescheduleSocialPost'),
    {
      idPost: postId,
      publishOn,
      timezoneOffset,
    },
    (r: any): void => {
      if (r.status === 'success') {
        window.PMW.redux.store.dispatch(setSocialPostDialogStatus(PostState.SCHEDULE_READY));
        window.PMW.redux.store.dispatch(setIsRescheduled(true));
        getPostById(postId, SocialPostEvent.SCHEDULED);
      } else {
        window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
      }
    }
  );
};

export const schedulePost = (postId: string, date: Date): void => {
  const timezoneOffset = date.getTimezoneOffset();
  const dateTimestamp = convertDateToUTCTimestamp(date);

  void window.PMW.write(
    window.PMW.util.site_url('socialmedia/rescheduleSocialPost'),
    {
      idPost: postId,
      publishOn: dateTimestamp,
      timezoneOffset,
    },
    (r: any): void => {
      if (r.status === 'success') {
        window.PMW.redux.store.dispatch(setSocialPostDialogStatus(PostState.SCHEDULE_READY));
        window.PMW.redux.store.dispatch(setIsRescheduled(true));
        getPostById(postId, SocialPostEvent.SCHEDULED);
      } else {
        window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
      }
    }
  );
};

export const isSocialMediaPostOwnedByCurrentUser = (post: SocialPost): boolean => {
  return post.userId === getUserId();
};
export const isSocialMediaPostSharedByTeamMember = (post: SocialPost): boolean => {
  return post.isSharedInTeam && post.userId !== getUserId();
};

export const shareSocialMediaPostInTeam = async (isSharedInTeam: boolean, idPost: string): Promise<void> => {
  if (isUserLoggedIn()) {
    await window.PMW.writeLocal('socialmedia/updateSocialMediaPostSharingStatus', {
      shareInTeam: isSharedInTeam,
      idPost: idPost,
    });
  }
};

export const addDuplicatedPostToGrid = (post: SocialPost): void => {
  window.PMW.redux.store.dispatch(addSocialPostToGrid(post));
};

export const duplicatePostAsDraft = async (post: SocialPost): Promise<SocialPost | undefined> => {
  window.PMW.setLoading(window.i18next.t('pmwjs_duplicating'));

  try {
    const data = (await window.PMW.writeLocal('socialmedia/duplicatePostAsDraft', {
      postId: post.id,
    })) as {idPost: string; post: SocialPost};

    window.PMW.setLoading(window.i18next.t('pmwjs_duplicated'), true);
    addDuplicatedPostToGrid(data.post);
    setTimeout(() => {
      window.PMW.setLoading(null);
    }, 3000);

    return data.post;
  } catch (e: any) {
    window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
    console.error(e);
    return undefined;
  }
};

export const duplicateSocialMediaPost = async (postToDuplicate: SocialPost): Promise<void> => {
  const duplicatedPost = await duplicatePostAsDraft(postToDuplicate);

  if (isSocialPostModalAlreadyOpen() && duplicatedPost) {
    openSocialPostsDialogModal({socialPost: duplicatedPost});
  }
};

const isSocialPostModalAlreadyOpen = (): boolean => {
  return isModalOpen();
};

export const triggerSocialPostEvent = (eventType: SocialPostEvent, detailObject: any): void => {
  window.dispatchEvent(
    new CustomEvent(eventType, {
      detail: detailObject,
    })
  );
};

export const triggerPublishingStatusSync = (isError: boolean, post: SocialPost): void => {
  const updatedPost: SocialPost = {...post, state: isError ? PostState.ERROR : PostState.PUBLISHED};

  triggerSocialPostEvent(SocialPostEvent.ERROR, {
    postId: post.id,
    post: updatedPost,
  });
};

const openSocialPostsDialog = (post: SocialPost): void => {
  const modalProps: SocialPostsDialogModalProps = {socialPost: post};
  openSocialPostsDialogModal(modalProps);
};

export const openModalForSocialPostById = (postId: string): void => {
  void window.PMW.write(
    window.PMW.util.site_url('socialmedia/getPostById'),
    {
      idPost: postId,
    },
    (r: any) => {
      if (r.status === 'success') {
        const {post} = r.data;
        openSocialPostsDialog(post);
      } else {
        window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
      }
    }
  );
};

const triggerSchedulingTrackingEvent = (accounts: SocialAccount[]): void => {
  for (const account of accounts) {
    const platform = account.type;
    const platformName = getSocialPlatformName(platform);

    trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: platformName});
    if (isPromotingEvent()) {
      trackEventPromotionScheduledForPlatform(platformName);
    }
  }
};

interface GetPostByIdResponse {
  post: SocialPost;
}

const isGetPostByIdResponse = (data: unknown): data is GetPostByIdResponse => {
  return typeof data === 'object' && data !== null && 'post' in data;
};

const fetchPostById = async (idPost: string): Promise<SocialPost | null> => {
  const response: unknown = await window.PMW.writeLocal('socialmedia/getPostById', {idPost});
  if (!isGetPostByIdResponse(response)) {
    return null;
  }

  return response.post;
};

export const getPostById = (postId: string, triggerEvent?: SocialPostEvent): void => {
  void fetchPostById(postId)
    .then((post) => {
      if (post === null) {
        window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
        return;
      }

      if (triggerEvent === undefined) {
        return;
      }

      if (triggerEvent === SocialPostEvent.SCHEDULED) {
        triggerSchedulingTrackingEvent(post.accounts);
      }

      triggerSocialPostEvent(triggerEvent, {postId, post});
    })
    .catch((e: unknown) => {
      window.PMW.setLoading(window.i18next.t('pmwjs_something_went_wrong'), true);
      console.error(e);
    });
};

export const refreshPostById = async (idPost: string): Promise<void> => {
  const post = await fetchPostById(idPost);
  if (post === null) {
    console.error(`refreshPostById: malformed response fetching post ${idPost}`);
    return;
  }

  // Update the grid's Redux source too, not just live event listeners, so a post's row stays fresh after
  // the components that setPost locally (grid item, dialog, resolve modal) unmount or re-render from store.
  window.PMW.redux.store.dispatch(mergeRefreshedPosts([post]));
  triggerSocialPostEvent(SocialPostEvent.UPDATED, {postId: idPost, post});
};

export const restoreSocialMediaPost = (idPost?: string): void => {
  window.PMW.setLoading(window.i18next.t('pmwjs_loading'));
  void window.PMW.write(window.PMW.util.site_url('socialmedia/restoreSocialPost'), {
    idPost,
  }).then((data: AjaxSuccessResponse) => {
    if (data.status !== 'success') {
      alert(window.i18next.t('pmwjs_error_message'));
    }
  });
};

export const openSocialMediaPost = (idPost?: string): void => {
  window.location.href = window.PMW.util.site_url(`socialmedia/post/${idPost}`);
};

export const getCaptionForSocialPost = (post: SocialPost): string => {
  if (post.publishingParams && Object.keys(post.publishingParams).length !== 0) {
    const firstAccountId = post.accounts[0].id;
    const firstCaption = post.publishingParams[firstAccountId]?.POST?.caption ?? '';

    if (firstCaption === '') {
      return window.i18next.t('pmwjs_no_caption');
    }

    return firstCaption;
  }

  if (post.caption) {
    return post.caption;
  }

  return window.i18next.t('pmwjs_no_caption');
};

export const isUserSocialPostCreator = (userId: number, post: SocialPost): boolean => {
  return post.userId === userId;
};
